/** Simple dropdown select. Wraps React Aria Select. */
import {
  Select as AriaSelect,
  SelectValue,
  Button,
  ListBox,
  ListBoxItem,
  Popover,
  Label,
  type SelectProps as AriaSelectProps,
  type Key,
} from 'react-aria-components';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/foundation/utils/cn';

export interface SelectItem {
  id: string;
  label: string;
}

export interface SelectProps<T extends SelectItem> extends Omit<AriaSelectProps<T>, 'children'> {
  label?: string;
  items: T[];
  className?: string;
  /** CSS class applied to the trigger button. */
  triggerClassName?: string;
  children?: (item: T) => React.ReactNode;
}

export function Select<T extends SelectItem>({
  label,
  items,
  className,
  triggerClassName,
  children,
  ...props
}: SelectProps<T>) {
  return (
    <AriaSelect className={cn('flex flex-col gap-1', className)} {...props}>
      {label && (
        <Label className="block text-[length:var(--f-text-sm)] font-medium text-[var(--f-fg-secondary)]">
          {label}
        </Label>
      )}
      <Button
        className={cn(
          'flex w-full items-center justify-between rounded-xl border border-[var(--f-border-strong)] px-3 py-2',
          'bg-[var(--f-bg-raised)] text-[var(--f-fg)] text-[length:var(--f-text-sm)]',
          'focus:outline-none focus:ring-2 focus:ring-[var(--f-accent)] focus:border-transparent',
          'transition-all cursor-pointer',
          triggerClassName
        )}
      >
        <SelectValue className="truncate" />
        <ChevronDown className="ml-2 h-4 w-4 shrink-0 text-[var(--f-fg-muted)]" />
      </Button>
      <Popover
        className={cn(
          'w-[var(--trigger-width)] overflow-auto rounded-xl border border-[var(--f-border-strong)]',
          'bg-[var(--f-bg-raised)] shadow-lg max-h-60'
        )}
      >
        <ListBox className="outline-none p-1" items={items}>
          {children ??
            ((item: T) => (
              <ListBoxItem
                key={item.id}
                id={item.id as Key}
                className={cn(
                  'px-3 py-2 rounded-lg cursor-pointer outline-none text-[length:var(--f-text-sm)]',
                  'hover:bg-[var(--f-bg-sunken)] focus:bg-[var(--f-bg-sunken)]',
                  'data-[selected]:bg-[var(--f-accent-subtle)] data-[selected]:text-[var(--f-accent-text)]'
                )}
              >
                {item.label}
              </ListBoxItem>
            ))}
        </ListBox>
      </Popover>
    </AriaSelect>
  );
}
